import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import {
  setLeftMenuContent,
  setSelectedSectionAndPage,
  updateTabPath,
  updateTabStates,
} from "../../store/actions";
import { Auth } from "aws-amplify";
import {
  getSurveyResultsWithUserFromAPI,
  getSurveyResultsFromAPI,
} from "../../services/surveyResultService";
import Summary from "../../components/surveyDetail/summary";
import Design from "../../components/surveyDetail/design";
import CollectOverview from "../../components/surveyDetail/collectOverview";
import CollectRecipientList from "../../components/surveyDetail/collectRecipientList";
import CollectImportRecipientList from "../../components/surveyDetail/collectImportRecipientList";
import CollectImportFromSurvey from "../../components/surveyDetail/collectImportFromSurvey";
import CollectEmailTemplate from "../../components/surveyDetail/collectEmailTemplate";
import CollectSchedule from "../../components/surveyDetail/collectSchedule";
import Results from "../../components/surveyDetail/results";
import Insight from "../../components/surveyDetail/insight";
import InsightSankey from "../../components/surveyDetail/insightSankey";
import { getReportsFromAPI } from "../../services/reportService";
import {
  getSurveyDetailFromAPI,
  updateSurveyFromAPI,
  getSurveysFromAPI,
} from "../../services/surveyService";
import {
  setSurveys,
  setUserSurveys,
  setOrganizations,
} from "../../store/actions";
import StrengthsWeaknesses from "../../components/surveyDetail/strengthsWeaknesses";
import GapAnalysis from "../../components/surveyDetail/gapAnalysis";
import MainReport from "../../components/surveyDetail/mainReport";
import HeatMap from "../../components/surveyDetail/heatMap";
import Findings from "../../components/surveyDetail/findings";
import Recommendations from "../../components/surveyDetail/recommendations";
import Roadmap from "../../components/surveyDetail/roadmap";
import BenchmarkedDescription from "../../components/surveyDetail/benchmarkedDescription";
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SurveyDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { survey_id } = useParams();
  const themeMode = useSelector((state) => state.themeMode);
  const topMenuTabs = useSelector((state) => state.topMenuTabs, shallowEqual);
  const currentSurvey = topMenuTabs.tabs.find(
    (tab) => tab.id === topMenuTabs.activeTab
  ).states.currentSurvey;
  const currentSurveyResult = topMenuTabs.tabs.find(
    (tab) => tab.id === topMenuTabs.activeTab
  ).states.currentSurveyResult;
  const leftMenuContent = topMenuTabs.tabs.find(
    (tab) => tab.id === topMenuTabs.activeTab
  ).states.leftMenuContent;
  let [token, setToken] = useState("");
  let [selectedFunction, setSelectedFunction] = useState("");
  const userSelectRef = useRef(null);
  async function checkAuth() {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const currentSession = await Auth.currentSession();
      await cognitoUser.refreshSession(
        currentSession.refreshToken,
        (err, session) => {
          const { idToken, refreshToken, accessToken } = session;
          getToken(idToken.jwtToken);
        }
      );
    } catch (e) {}
  }
  const [lastSegment,setLastSegment]=useState("")
  useEffect(() => {
    getToken();
    const urlSegments = location.pathname.split("/");
    const lastSegment = urlSegments[urlSegments.length - 1];
    setLastSegment(lastSegment.replace("#", ""))
    let _selectedTabName = "";
    if (lastSegment.startsWith("summary")) {
      _selectedTabName = "Summary";
    } else if (lastSegment.includes("design")) {
      _selectedTabName = "Design";
    } else if (lastSegment.includes("collect")) {
      _selectedTabName = "Collect";
    } else if (lastSegment.includes("results")) {
      _selectedTabName = "Results";
    } else if (lastSegment.startsWith("insight-")) {
      _selectedTabName = "Insight";
    }
    setTabs((prevTabs) =>
      prevTabs.map((tab) => ({
        ...tab,
        current: tab.name === _selectedTabName,
      }))
    );
  }, [location.pathname]);

  useEffect(() => {
    if (token.length > 0 && currentSurvey.ID !== undefined) {
      if (selectedFunction === "getResults") {
        getResults();
        getInsightData();
        viewSurveyProgress();
      }
    } else if (token.length > 0 && survey_id.length > 0) {
      getSurveys();
      getResults();
      getInsightData();
      viewSurveyProgress();
    }
  }, [token, currentSurvey]);

  async function getToken(token) {
    if (token) {
      setToken(token);
    } else {
      const currentSession = await Auth.currentSession();
      setToken(currentSession.idToken.jwtToken);
      setSelectedFunction("getResults");
    }
  }

  async function getSurveys() {
    try {
      const result = await getSurveysFromAPI(token);
      result["surveys"].forEach((survey) => {
        survey["responded"] =
          result["userSurveyItems"].filter(
            (item) => item.survey_id === survey.ID && item.is_completed
          ).length +
          "/" +
          result["userSurveyItems"].filter(
            (item) => item.survey_id === survey.ID
          ).length +
          " Responded";
      });
      dispatch(
        setSurveys(
          result["surveys"].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
        )
      );
      dispatch(setUserSurveys(result["userSurveyItems"]));
      dispatch(setOrganizations(result["organizations"]));
      if (survey_id) {
        dispatch(
          updateTabStates(
            topMenuTabs.activeTab,
            "currentSurvey",
            result["surveys"].find((survey) => survey.ID === survey_id)
          )
        );
        if (location.pathname.includes("/design")) {
          dispatch(
            updateTabStates(
              topMenuTabs.activeTab,
              "surveyDetailSelectedTab",
              "Design"
            )
          );
          dispatch(
            updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Design")
          );
        } else if (location.pathname.includes("/results")) {
          dispatch(
            updateTabStates(
              topMenuTabs.activeTab,
              "surveyDetailSelectedTab",
              "Results"
            )
          );
          dispatch(
            updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Results")
          );
        } else if (
          location.pathname.includes("/insight") ||
          location.pathname.includes("/download-reports")
        ) {
          dispatch(
            updateTabStates(
              topMenuTabs.activeTab,
              "surveyDetailSelectedTab",
              "Insight"
            )
          );
          dispatch(
            updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Insight")
          );
        } else if (location.pathname.includes("/collect")) {
          dispatch(
            updateTabStates(
              topMenuTabs.activeTab,
              "surveyDetailSelectedTab",
              "Collect"
            )
          );
          dispatch(
            updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Collect")
          );
        } else {
          dispatch(
            updateTabStates(
              topMenuTabs.activeTab,
              "surveyDetailSelectedTab",
              "Summary"
            )
          );
        }
      }
    } catch (error) {
      if (error.message === "Request failed with status code 401") {
        setSelectedFunction("getSurveys");
        checkAuth();
      }
      console.error("Error fetching data:", error);
    }
  }

  async function getResults() {
    let _survey_id =
      currentSurvey.ID !== undefined ? currentSurvey.ID : survey_id;
    try {
      dispatch(
        updateTabStates(topMenuTabs.activeTab, "currentSurveyResult", [])
      );
      const result = await getSurveyResultsFromAPI(_survey_id);
      dispatch(
        updateTabStates(
          topMenuTabs.activeTab,
          "currentSurveyResult",
          result["results"]
        )
      );
    } catch (error) {
      if (error.message === "Request failed with status code 401") {
        setSelectedFunction("getResults");
        checkAuth();
      }
    }
  }

  async function getInsightData() {
    let _survey_id =
      currentSurvey.ID !== undefined ? currentSurvey.ID : survey_id;
    try {
      dispatch(
        updateTabStates(topMenuTabs.activeTab, "currentSurveyInsightData", {})
      );
      const result = await getReportsFromAPI(token, _survey_id);
      dispatch(
        updateTabStates(
          topMenuTabs.activeTab,
          "currentSurveyInsightData",
          result
        )
      );
    } catch (error) {
      if (error.message === "Request failed with status code 401") {
        checkAuth();
      }
    }
  }

  async function viewSurveyProgress() {
    let _survey_id =
      currentSurvey.ID !== undefined ? currentSurvey.ID : survey_id;
    dispatch(updateTabStates(topMenuTabs.activeTab, "surveyRecipients", []));
    await getSurveyDetailFromAPI(token, _survey_id)
      .then((result) => {
        dispatch(
          updateTabStates(
            topMenuTabs.activeTab,
            "surveyRecipients",
            result["userSurveyItems"]
          )
        );
      })
      .catch((error) => {
        console.log("get survey detail error : ", error);
      });
  }

  const [tabs, setTabs] = useState([
    {
      name: "Summary",
      href: "#",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M7 2a2 2 0 012 2v12a2 2 0 01-2 2H3a2 2 0 01-2-2V4a2 2 0 012-2h4zm6 0a2 2 0 012 2v12a2 2 0 01-2 2h-4a2 2 0 01-2-2V4a2 2 0 012-2h4z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
      current: true,
    },
    {
      name: "Design",
      href: "#",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M17.659 3.341a2 2 0 00-2.828 0L5 12.174V16h3.826l9.83-9.83a2 2 0 000-2.828l-1.5-1.5z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
      current: false,
    },
    {
      name: "Collect",
      href: "#",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
      current: false,
    },
    {
      name: "Results",
      href: "#",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.707a1 1 0 00-1.414 0L9 12.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l6-6a1 1 0 000-1.414z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
      current: false,
    },
    {
      name: "Insight",
      href: "#",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M10 2a8 8 0 11-8 8 8 8 0 018-8zm0 2a6 6 0 106 6 6 6 0 00-6-6zm1 8a1 1 0 10-2 0v1a1 1 0 002 0v-1zm-1-3a1 1 0 011 1 1 1 0 011 1 1 1 0 01-2 0 1 1 0 01-1-1 1 1 0 011-1z"
            clipRule="evenodd"
          ></path>
        </svg>
      ),
      current: false,
    },
  ]);

  useEffect(() => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) => ({
        ...tab,
        current: location.pathname
          .toLowerCase()
          .split("/")
          .filter(Boolean)
          .pop()
          .startsWith(tab.name.toLowerCase()),
      }))
    );
  }, [location.pathname]);

  const surveyDetailTabMenuClickHandler = (tabName) => {
    setTabs(
      tabs.map((tab) => {
        if (tab.name === tabName) tab.current = true;
        else tab.current = false;
        return tab;
      })
    );
    if (tabName === "Summary") {
      dispatch(
        updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Surveys")
      );
      dispatch(
        updateTabPath(
          topMenuTabs.activeTab,
          "Summary",
          `/surveys/${currentSurvey.ID}/summary`
        )
      );
      navigate("summary");
    } else if (tabName === "Design") {
      if (leftMenuContent !== "Design") {
        dispatch(
          updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Design")
        );
        dispatch(
          updateTabStates(topMenuTabs.activeTab, "selectedSectionAndPage", {})
        );
        dispatch(
          updateTabPath(
            topMenuTabs.activeTab,
            "Design",
            `/surveys/${currentSurvey.ID}/design`
          )
        );
        navigate("design");
      }
    } else if (tabName === "Collect") {
      dispatch(
        updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Collect")
      );
      dispatch(
        updateTabPath(
          topMenuTabs.activeTab,
          "Overview",
          `/surveys/${currentSurvey.ID}/collect-overview`
        )
      );
      navigate("collect-overview");
    } else if (tabName === "Results") {
      dispatch(
        updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Results")
      );
      dispatch(
        updateTabPath(
          topMenuTabs.activeTab,
          "Results",
          `/surveys/${currentSurvey.ID}/results`
        )
      );
      navigate("results");
    } else if (tabName === "Insight") {
      dispatch(
        updateTabStates(topMenuTabs.activeTab, "leftMenuContent", "Insight")
      );
      dispatch(
        updateTabPath(
          topMenuTabs.activeTab,
          "Insight",
          `/surveys/${currentSurvey.ID}/insight-summary`
        )
      );
      navigate("insight-summary");
    }
  };

  const [surveyNameInputValue, setSurveyNameInputValue] = useState("");

  useEffect(() => {
    if (currentSurvey && currentSurvey.survey_name !== surveyNameInputValue) {
      setSurveyNameInputValue(currentSurvey.survey_name);
    }
  }, [currentSurvey]);

  const handleInputChange = (e) => {
    setSurveyNameInputValue(e.target.value);
  };

  const handleEndEdit = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      const updatedSurvey = {
        ...currentSurvey,
        survey_name: surveyNameInputValue,
      };
      updateSurveyName(updatedSurvey);
    }
  };

  async function updateSurveyName(_currentSurvey) {
    try {
      const obj = {
        survey_name: _currentSurvey.survey_name,
      };
      await updateSurveyFromAPI(token, obj, _currentSurvey.ID)
        .then((result) => {
          // dispatch(setCurrentSurvey(_currentSurvey));
          dispatch(
            updateTabStates(
              topMenuTabs.activeTab,
              "currentSurvey",
              _currentSurvey
            )
          );
        })
        .catch((error) => {
          console.log("update survey error : ", error);
        });
    } catch (error) {
      console.log("update survey error : ", error);
    }
  }
  const [selectedUser, setSelectedUser] = useState(null);

  async function onchangeUser(user) {
    setIsOpenMenu(false);
    setSelectedUser(user);

    try {
      dispatch(updateTabStates(topMenuTabs.activeTab, "pageIsLoading", true));
      // dispatch(updateTabStates(topMenuTabs.activeTab, 'currentSurveyResult', []));
      const result = await getSurveyResultsWithUserFromAPI(
        currentSurvey.ID,
        user.user_id
      );
      console.log("Results:", result);
      dispatch(
        updateTabStates(
          topMenuTabs.activeTab,
          "currentSurveyResult",
          result["results"]
        )
      );
      dispatch(updateTabStates(topMenuTabs.activeTab, "pageIsLoading", false));
    } catch (error) {
      dispatch(updateTabStates(topMenuTabs.activeTab, "pageIsLoading", false));
      // if (error.message === "Request failed with status code 401") {
      //   setSelectedFunction("getResults");
      //   checkAuth();
      // }
    }
  }
  const handleClickOutside = (event, ref, setState) => {
    console.log("geldi", ref.current);
    if (ref.current && !ref.current.contains(event.target)) {
      console.log("yes");
      setState(false); // Eğer dışarıya tıklanırsa, ilgili modalı kapat
    }
  };
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  useEffect(() => {
    if (isOpenMenu) {
      document.addEventListener("click", (event) =>
        handleClickOutside(event, userSelectRef, setIsOpenMenu)
      );
    }

    return () => {
      document.removeEventListener("click", (event) =>
        handleClickOutside(event, userSelectRef, setIsOpenMenu)
      );
    };
  }, [isOpenMenu]);
  return (
    <div
      className={` w-full  pr-0 overflow-x-hidden overflow-y-hidden  ${
        themeMode === "dark" ? "bg-[#1a1a28]" : "bg-[#ffffff]"
      }`}
    >
      <div
        className={`flex justify-start text-center items-center text-white text-xl py-2 h-auto border-b border-solid  px-4 sm:px-13  
                    ${
                      themeMode === "dark"
                        ? "border-white-06"
                        : "border-black-06"
                    }
                `}
      >
        <p
          className={`bg-transparent ${
            themeMode === "dark" ? "text-white" : "text-black"
          } text-start border-none font-bold text-xs sm:text-sm`}
        >
          SURVEY TITLE:
        </p>
        <input
          id="editable-input"
          type="text"
          value={surveyNameInputValue || ""}
          onChange={handleInputChange}
          onBlur={handleEndEdit}
          onKeyDown={handleEndEdit}
          className={`bg-transparent ml-2 ${
            themeMode === "dark"
              ? "text-white placeholder:text-white"
              : "text-black  placeholder:text-black"
          } text-start border-none outline-none font-bold text-xs sm:text-sm min-w-[500px]`}
        />
      </div>
      <div
        className={`  overflow-x-auto overflow-y-hidden  flex  justify-between   sm:py-[0.32rem]  h-[30px] sm:h-[50px] border-b border-solid px-0 sm:px-13 ${
          themeMode === "dark" ? "border-white-06" : "border-black-06"
        } w-full`}
      >
        <nav
          aria-label="Tabs"
          className={`  flex items-center sm:w-max  justify-start sm:justify-center flex-nowrap flex-shrink-0 min-w-0 gap-[0.25rem] rounded-[0.375rem] w-full   ${
            themeMode === "dark" ? "bg-[#a5bdff0d]" : "bg-[#1530720d] "
          }`}
        >
          {tabs.map((tab, index) => (
            <a
              key={tab.name}
              href={tab.href}
              aria-current={tab.current ? "page" : undefined}
              onClick={() => surveyDetailTabMenuClickHandler(tab.name)}
              className={classNames(
                tab.current
                  ? themeMode === "dark"
                    ? "bg-[#A5BDFF26] text-[#FFFFFF] flex justify-center items-center flex-nowrap flex-shrink-0 rounded-[0.375rem] hover:text-[#FFFFFF]"
                    : "bg-[#A5BDFF26] text-[#5a667e] flex justify-center items-center flex-nowrap flex-shrink-0 rounded-[0.375rem] hover:text-[#000] "
                  : themeMode === "dark"
                  ? "flex justify-center items-center flex-nowrap flex-shrink-0 hover:rounded-[0.375rem] text-[#C1C9D6] hover:bg-[#a5bdff10] hover:text-[#FFFFFF]"
                  : "flex justify-center items-center flex-nowrap flex-shrink-0 hover:rounded-[0.375rem] text-[#5a667e] hover:bg-gray-200 hover-text-[#000]",
                // (Object.keys(currentSurveyResult).length === 0 && tab.name === "Insight") ? "cursor:auto pointer-events-none opacity-50" : "",
                "group inline-flex items-center  py-2 pl-2 pr-2 text-xs sm:text-sm font-medium w-auto sm:min-w-[4.5rem] sm:max-w-[4.9rem] flex flex-1 sm:flex-none"
              )}
            >
              <span>{tab.name}</span>
            </a>
          ))}
        </nav>
        {lastSegment === "results" && 
        <div className=" flex flex-row  justify-between items-center ">
          <div className="flex flex-col" ref={userSelectRef}>
            <button
              onClick={() => {
                setIsOpenMenu(!isOpenMenu);
              }}
              className={`hulyButton font-medium-14 h-[1.5rem] lg:h-[2rem] w-64 px-2 border rounded-[0.375rem] cursor-pointer flex justify-between  items-center gap-2 ${
                themeMode === "dark"
                  ? "border-[#d1d5de0a] bg-[#d1d5de0d] hover:bg-[#d1d5de0a] hover:text-[#fff] text-white-8"
                  : "border-[#1725471a] bg-[#1725470d] hover:bg-[#1725471a] hover:text-[#000] text-black-8"
              }`}
            >
              <div className="truncate">
                {selectedUser
                  ? selectedUser.first_name + " " + selectedUser.last_name
                  : "Select an user"}
              </div>
              {selectedUser ? (
                <div
                  className={`btn-icon  ${
                    themeMode === "dark" ? "text-white-8" : "text-black-8"
                  }`}
                  onClick={(event) => {
                    event.stopPropagation(); // Parent button'a click eventinin gitmesini engeller
                    setSelectedUser(null);
                    getResults();
                  }}
                >
                  <svg
                    className="svg-small w-[.8rem] h-[.8rem] mr-1"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8.7,8l4.6-4.6c0.2-0.2,0.2-0.5,0-0.7s-0.5-0.2-0.7,0L8,7.3L3.4,2.6c-0.2-0.2-0.5-0.2-0.7,0s-0.2,0.5,0,0.7L7.3,8l-4.6,4.6c-0.2,0.2-0.2,0.5,0,0.7c0.1,0.1,0.2,0.1,0.4,0.1s0.3,0,0.4-0.1L8,8.7l4.6,4.6c0.1,0.1,0.2,0.1,0.4,0.1s0.3,0,0.4-0.1c0.2-0.2,0.2-0.5,0-0.7L8.7,8z"></path>
                  </svg>
                </div>
              ) : (
                <svg
                  className="w-5 h-5 ml-2 -mr-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          </div>
          {isOpenMenu && (
            <div
              className={`absolute right-13 z-[99999999]  w-64 top-[120px]  ${
                currentSurvey?.survey_users?.length <= 6
                  ? "h-auto"
                  : "h-[250px]"
              } overflow-y-scroll  origin-top-right rounded-md shadow-2xl ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in  border-solid border-[1px] ${
                themeMode === "dark"
                  ? " bg-[#2a2939] border-white-09 shadow-theme-popup-shadow "
                  : "bg-[#FFFFFF] border-black-09 shadow-black-2-shadow"
              }`}
            >
              <div className="py-1">
                {currentSurvey?.survey_users?.map((user) => (
                  <button
                    key={user.user_id}
                    className={`flex-shrink-0 flex justify-between px-2 py-1 min-w-0 min-h-[2.25rem] text-left rounded-md cursor-pointer ${
                      themeMode === "dark"
                        ? "hover:bg-[#333240] text-white"
                        : "hover:bg-[#EBEBEB] text-black"
                    }`}
                    onClick={() => {
                      onchangeUser(user);
                    }}
                  >
                    {user.first_name} {user.last_name} - {user.email}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>}
      </div>
      {currentSurvey.ID !== undefined && (
        <Routes>
          <Route
            path="summary"
            element={<Summary className="overflow-y-hidden" />}
          />
          <Route path="design" element={<Design />} />
          <Route path="collect-overview" element={<CollectOverview />} />
          <Route
            path="collect-recipient-list"
            element={<CollectRecipientList />}
          />
          <Route
            path="collect-import-recipient-list"
            element={<CollectImportRecipientList />}
          />
          <Route
            path="collect-import-from-survey"
            element={<CollectImportFromSurvey />}
          />
          <Route
            path="collect-email-template"
            element={<CollectEmailTemplate />}
          />
          <Route path="collect-schedule" element={<CollectSchedule />} />
          <Route path="results" element={<Results />} />
          <Route path="insight-summary" element={<Insight />} />
          <Route path="insight-main-report" element={<MainReport />} />
          <Route path="insight-sankey" element={<InsightSankey />} />
          {/* <Route path="download-reports" element={<DownloadReports />} /> */}
          <Route
            path="insight-strengths-weaknesses"
            element={<StrengthsWeaknesses />}
          />
          <Route path="insight-gap-analysis" element={<GapAnalysis />} />
          <Route
            path="insight-benchmarked-description"
            element={<BenchmarkedDescription />}
          />
          <Route path="insight-heatmap" element={<HeatMap />} />
          <Route path="insight-findings" element={<Findings />} />
          <Route
            path="insight-recommendations-roadmap"
            element={<Recommendations />}
          />
          {/* <Route path="roadmap" element={<Roadmap />} /> */}
        </Routes>
      )}
    </div>
  );
};

export default SurveyDetail;
